import { SetSelectionCommand } from "../commands/SetSelectionCommand";
import { Selection } from "../selection/Selection";
import { screenToDoc } from "../render/Viewport";
import type { KeyModifiers, PointerSample, Tool, ToolContext } from "./Tool";

// Rectangular / Elliptical Marquee. Photoshop shortcut M (cycle through
// shapes). A single class powers both shapes; pick which one via the
// constructor `shape` field.
//
// Behaviors:
//   - Drag from anchor → current pointer (doc space).
//   - Shift held during drag → constrain to square / circle.
//   - Alt held during drag → drag from center, not corner.
//     (These are evaluated continuously while dragging, mirroring Photoshop.)
//   - Modifier keys held at pointer-down decide the combine mode against the
//     document's existing selection:
//       default (no shift, no alt) → replace
//       shift only                 → add (union)
//       alt only                   → subtract
//       shift + alt                → intersect
//   - If pointer is INSIDE existing selection + no modifiers → MOVE selection.
//   - During drag, emit transient SetSelectionCommand for live preview.
//   - On pointer up, emit a real SetSelectionCommand for the undo entry.
//   - On Escape (or cancel()), restore the original selection transiently.
//
// No DOM access — Escape handling is wired by the host via cancel().

export type MarqueeShape = "rect" | "ellipse" | "single-row" | "single-column";

export type CombineMode = "replace" | "add" | "subtract" | "intersect";

export interface MarqueeToolOptions {
  readonly id: string;
  readonly shortcut: string;
  readonly shape: MarqueeShape;
}

interface Drag {
  anchorDocX: number;
  anchorDocY: number;
  readonly originalSelection: Selection | null;
  readonly combine: CombineMode;
  readonly docW: number;
  readonly docH: number;
  // last preview region (in doc space) used to debounce emissions
  lastX: number;
  lastY: number;
  lastW: number;
  lastH: number;
  lastShift: boolean;
  lastAlt: boolean;
  emitted: boolean;
  // Space-drag reposition state
  spaceHeld: boolean;
  /** Pointer doc coords at the moment Space was pressed (to compute offset). */
  spaceAnchorX: number;
  spaceAnchorY: number;
  /** anchorDocX/Y saved at Space press so we can shift it on Space release. */
  savedAnchorX: number;
  savedAnchorY: number;
  /** Last pointer doc coords seen during Space-hold, so we can finalize anchor. */
  lastPointerDocX: number;
  lastPointerDocY: number;
}

/** "Move selection" mode — dragging inside an existing selection boundary. */
interface MoveSelectionDrag {
  readonly originalSelection: Selection;
  readonly anchorDocX: number;
  readonly anchorDocY: number;
  readonly docW: number;
  readonly docH: number;
  lastDx: number;
  lastDy: number;
}

// Threshold for "inside selection" — must be mostly selected (not just edge).
const MOVE_SELECTION_THRESHOLD = 128;

export class MarqueeTool implements Tool {
  readonly id: string;
  readonly shortcut: string;
  readonly shape: MarqueeShape;

  /** Feather radius in doc pixels (applied on pointer-up). */
  feather = 0;
  /**
   * Whether to anti-alias the selection edges. Photoshop's Rectangular
   * Marquee has no Anti-alias checkbox at all — rectangular selections are
   * always hard-edged; only the Elliptical Marquee exposes the option
   * (defaulting on). ToolUX's options-bar already hides the checkbox for
   * "marquee-rect" (see MarqueeToolOptions, i156), but this field's default
   * still needs to match that per-shape behavior since nothing else can ever
   * set it for rect/row/column shapes. Set in the constructor below (must be
   * shape-dependent, so it can't be a plain field initializer — `this.shape`
   * isn't assigned yet when field initializers run).
   */
  antiAlias: boolean;
  /** Selection style: normal, fixed-ratio, or fixed-size. */
  style: "normal" | "fixed-ratio" | "fixed-size" = "normal";
  /** Fixed width for fixed-ratio or fixed-size modes (in doc pixels). */
  fixedWidth = 100;
  /** Fixed height for fixed-ratio or fixed-size modes (in doc pixels). */
  fixedHeight = 100;
  /**
   * Explicit combine-mode override set by the options bar's New/Add/
   * Subtract/Intersect button strip (ToolUX's wireCombineModeButtons,
   * audit i60/i162). When set to "add"/"subtract"/"intersect" it wins over
   * the shift/alt-derived default computed at pointer-down; "new" (or
   * unset) falls back to the existing shift/alt modifier behavior.
   */
  combineMode?: "new" | "add" | "subtract" | "intersect";

  private drag: Drag | null = null;
  private moveDrag: MoveSelectionDrag | null = null;

  constructor(opts: MarqueeToolOptions) {
    this.id = opts.id;
    this.shortcut = opts.shortcut;
    this.shape = opts.shape;
    // i156: only the elliptical marquee anti-aliases by default — rect,
    // single-row, and single-column marquees are always hard-edged in
    // Photoshop (and have no options-bar checkbox to turn AA on).
    this.antiAlias = opts.shape === "ellipse";
  }

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);

    // Check if clicking inside an existing selection with no modifiers → move mode
    const sel = ctx.doc.selection;
    if (sel && !s.shiftKey && !s.altKey && isInsideSelection(sel, p.x, p.y) > MOVE_SELECTION_THRESHOLD) {
      this.moveDrag = {
        originalSelection: sel,
        anchorDocX: p.x,
        anchorDocY: p.y,
        docW: ctx.doc.width,
        docH: ctx.doc.height,
        lastDx: NaN,
        lastDy: NaN,
      };
      return;
    }

    // Snap the anchor corner to guides/grid/other-layer edges/document bounds
    // (audit i26) so a freshly-started marquee can start flush against a target.
    const anchor = ctx.snapPoint?.(p.x, p.y) ?? p;

    const combine = pickCombine(s.shiftKey, s.altKey, this.combineMode);
    this.drag = {
      anchorDocX: anchor.x,
      anchorDocY: anchor.y,
      originalSelection: sel,
      combine,
      docW: ctx.doc.width,
      docH: ctx.doc.height,
      lastX: NaN,
      lastY: NaN,
      lastW: NaN,
      lastH: NaN,
      lastShift: s.shiftKey,
      lastAlt: s.altKey,
      emitted: false,
      spaceHeld: false,
      spaceAnchorX: 0,
      spaceAnchorY: 0,
      savedAnchorX: anchor.x,
      savedAnchorY: anchor.y,
      lastPointerDocX: p.x,
      lastPointerDocY: p.y,
    };
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (this.moveDrag) {
      this.onMoveSelectionDrag(ctx, s);
      return;
    }
    if (!this.drag) return;
    const d = this.drag;
    const p = screenToDoc(ctx.viewport, s.x, s.y);

    // Track the latest pointer position for Space-drag finalization.
    d.lastPointerDocX = p.x;
    d.lastPointerDocY = p.y;

    // While Space is held, move the in-progress anchor instead of resizing.
    if (d.spaceHeld) {
      const offsetX = p.x - d.spaceAnchorX;
      const offsetY = p.y - d.spaceAnchorY;
      d.anchorDocX = d.savedAnchorX + offsetX;
      d.anchorDocY = d.savedAnchorY + offsetY;
    }

    // Snap the moving corner to guides/grid/other-layer edges/document bounds
    // (audit i26) — but not while Space-repositioning, where the box must
    // keep a constant size as both corners translate together.
    let cornerX = p.x;
    let cornerY = p.y;
    if (!d.spaceHeld) {
      const snapped = ctx.snapPoint?.(p.x, p.y);
      if (snapped) {
        cornerX = snapped.x;
        cornerY = snapped.y;
      }
    }

    const region = computeRegion(
      d.anchorDocX,
      d.anchorDocY,
      cornerX,
      cornerY,
      s.shiftKey,
      s.altKey,
      this.style,
      this.fixedWidth,
      this.fixedHeight,
    );
    // matches the last emission, skip work.
    if (
      region.x === d.lastX &&
      region.y === d.lastY &&
      region.w === d.lastW &&
      region.h === d.lastH &&
      s.shiftKey === d.lastShift &&
      s.altKey === d.lastAlt
    ) {
      return;
    }
    d.lastX = region.x;
    d.lastY = region.y;
    d.lastW = region.w;
    d.lastH = region.h;
    d.lastShift = s.shiftKey;
    d.lastAlt = s.altKey;
    d.emitted = true;
    const preview = this.buildSelection(d, region);
    ctx.emitTransient(new SetSelectionCommand(preview));
  }

  /** Move the selection boundary by the drag offset. */
  private onMoveSelectionDrag(ctx: ToolContext, s: PointerSample): void {
    const d = this.moveDrag;
    if (!d) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const dx = Math.round(p.x - d.anchorDocX);
    const dy = Math.round(p.y - d.anchorDocY);

    // Debounce — skip if offset hasn't changed.
    if (dx === d.lastDx && dy === d.lastDy) return;
    d.lastDx = dx;
    d.lastDy = dy;

    const moved = d.originalSelection.translate(dx, dy);
    ctx.emitTransient(new SetSelectionCommand(moved));
  }

  onPointerUp(ctx: ToolContext, s: PointerSample): void {
    if (this.moveDrag) {
      this.onMoveSelectionUp(ctx, s);
      return;
    }
    if (!this.drag) return;
    const d = this.drag;
    this.drag = null;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    // Snap the final corner the same way onPointerMove does (audit i26).
    const snappedUp = ctx.snapPoint?.(p.x, p.y);
    const upX = snappedUp ? snappedUp.x : p.x;
    const upY = snappedUp ? snappedUp.y : p.y;
    const region = computeRegion(
      d.anchorDocX,
      d.anchorDocY,
      upX,
      upY,
      s.shiftKey,
      s.altKey,
      this.style,
      this.fixedWidth,
      this.fixedHeight,
    );
    // Zero-area drag (single click or sub-2px drag).
    // Photoshop: a plain click (no Shift/Alt) DESELECTS the current selection —
    // including clicks on the pasteboard outside the canvas. Shift/Alt intent
    // (add/subtract modes) keeps the existing selection intact instead.
    if (region.w <= 0 || region.h <= 0) {
      if (d.combine === "replace") {
        // Deselect only when there is actually something to deselect.
        // Emit null so the action is undoable; skip when already clear to
        // avoid a no-op undo entry.
        if (d.originalSelection !== null) {
          ctx.emit(new SetSelectionCommand(null));
        }
      } else if (d.emitted) {
        // Add/subtract intent — re-anchor to original (no change desired).
        ctx.emit(new SetSelectionCommand(d.originalSelection));
      }
      return;
    }
    const next = this.buildSelection(d, region);
    // Apply feather if configured (Photoshop default: feather is applied at commit time).
    const feathered = next && this.feather > 0 ? next.feather(this.feather) : next;
    if (!feathered) {
      // The drag produced a real (non-zero-area) region, but the combine
      // (typically Intersect with no overlap, or Subtract that fully
      // consumed the existing selection) left nothing selected (audit i158).
      ctx.showTip("No pixels were selected.");
    }
    ctx.emit(new SetSelectionCommand(feathered));
  }

  /** Commit the moved selection. */
  private onMoveSelectionUp(ctx: ToolContext, s: PointerSample): void {
    const d = this.moveDrag;
    this.moveDrag = null;
    if (!d) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const dx = Math.round(p.x - d.anchorDocX);
    const dy = Math.round(p.y - d.anchorDocY);
    if (dx === 0 && dy === 0) return; // no actual move
    const moved = d.originalSelection.translate(dx, dy);
    ctx.emit(new SetSelectionCommand(moved));
  }

  /**
   * Handle Space keydown / keyup to enter/exit marquee-reposition mode.
   * When Space is pressed mid-drag the anchor moves with the pointer instead
   * of resizing the selection. Releasing Space resumes normal sizing.
   *
   * key should be " " (space) on press or "SpaceUp" on release; the host is
   * responsible for synthesising the release signal.
   */
  /** Return true when a pointer drag is in progress (for host Space/Escape routing). */
  isDragging(): boolean {
    return this.drag !== null || this.moveDrag !== null;
  }

  onKeyDown(ctx: ToolContext, key: string, modifiers?: KeyModifiers): boolean | void {
    if (!this.drag) return false; // not dragging — ignore (host handles hand-tool switch)
    const d = this.drag;

    if (key === " " && !d.spaceHeld) {
      // Enter reposition mode: save current anchor and pointer position.
      d.spaceHeld = true;
      d.savedAnchorX = d.anchorDocX;
      d.savedAnchorY = d.anchorDocY;
      d.spaceAnchorX = d.lastPointerDocX;
      d.spaceAnchorY = d.lastPointerDocY;
      return true; // consumed
    }
    if (key === "SpaceUp" && d.spaceHeld) {
      // Exit reposition mode: anchor has already been updated by onPointerMove.
      d.spaceHeld = false;
      return true; // consumed
    }
    return false;
  }

  /**
   * Cancel the in-progress drag (call from Escape handler in the host). The
   * original selection is re-emitted transiently so the live preview reverts.
   */
  cancel(ctx: ToolContext): void {
    if (this.moveDrag) {
      const original = this.moveDrag.originalSelection;
      this.moveDrag = null;
      ctx.emitTransient(new SetSelectionCommand(original));
      return;
    }
    if (!this.drag) return;
    const original = this.drag.originalSelection;
    this.drag = null;
    ctx.emitTransient(new SetSelectionCommand(original));
  }

  private buildSelection(d: Drag, region: Region): Selection | null {
    const local = this.shapeSelection(d.docW, d.docH, region);
    const original = d.originalSelection;
    let result: Selection | null;
    switch (d.combine) {
      case "replace":
        result = local.isEmpty() ? null : local;
        break;
      case "add":
        if (!original) result = local.isEmpty() ? null : local;
        else if (local.isEmpty()) result = original;
        else result = original.union(local);
        break;
      case "subtract":
        if (!original) result = null;
        else if (local.isEmpty()) result = original;
        else result = original.subtract(local);
        break;
      case "intersect":
        if (!original || local.isEmpty()) result = null;
        else result = original.intersect(local);
        break;
    }
    // Normalize: combine ops (notably "intersect" when the marquee and
    // existing selection bounds don't actually overlap, and "subtract" when
    // it fully consumes the original) can yield a non-null Selection whose
    // mask is entirely zero. Treat that the same as "no selection" so
    // downstream isEmpty()/truthy checks agree (audit i158).
    return result && result.isEmpty() ? null : result;
  }

  private shapeSelection(docW: number, docH: number, r: Region): Selection {
    if (this.shape === "rect") {
      return Selection.fromRect(docW, docH, r.x, r.y, r.w, r.h, this.antiAlias);
    }
    if (this.shape === "single-row") {
      const row = Math.round(r.y + r.h / 2);
      return Selection.fromSingleRow(docW, docH, row);
    }
    if (this.shape === "single-column") {
      const col = Math.round(r.x + r.w / 2);
      return Selection.fromSingleColumn(docW, docH, col);
    }
    const cx = r.x + r.w / 2;
    const cy = r.y + r.h / 2;
    const rx = r.w / 2;
    const ry = r.h / 2;
    return Selection.fromEllipse(docW, docH, cx, cy, rx, ry, this.antiAlias);
  }
}

interface Region {
  x: number;
  y: number;
  w: number;
  h: number;
}

function pickCombine(shift: boolean, alt: boolean, override?: string): CombineMode {
  // An explicit options-bar combine-mode selection (audit i60/i162) wins over
  // the shift/alt-derived default. "new" (or no override) falls through to
  // the existing modifier-key behavior below.
  if (override === "add" || override === "subtract" || override === "intersect") {
    return override;
  }
  if (shift && alt) return "intersect";
  if (shift) return "add";
  if (alt) return "subtract";
  return "replace";
}

/**
 * Sample the selection mask at fractional doc coordinates by checking all 4
 * surrounding integer pixels and returning the maximum value. This avoids the
 * bug where `screenToDoc` returns fractional coords that truncate (via `| 0`)
 * to an anti-aliased edge pixel with value < threshold, even though the click
 * is visually inside the selection.
 */
function isInsideSelection(
  sel: Selection,
  x: number,
  y: number,
): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  let maxVal = 0;
  for (let dy = 0; dy <= 1; dy++) {
    for (let dx = 0; dx <= 1; dx++) {
      const v = sel.contains(xi + dx, yi + dy);
      if (v > maxVal) maxVal = v;
    }
  }
  return maxVal;
}

// Compute the active rectangle from anchor → pointer in doc space, applying
// constrain (square) and from-center modifiers as live state.
function computeRegion(
  ax: number,
  ay: number,
  px: number,
  py: number,
  constrain: boolean,
  fromCenter: boolean,
  style: "normal" | "fixed-ratio" | "fixed-size",
  fixedWidth: number,
  fixedHeight: number,
): Region {
  // Fixed-size mode: use fixed dimensions regardless of drag distance.
  if (style === "fixed-size") {
    const w = Math.max(1, fixedWidth);
    const h = Math.max(1, fixedHeight);
    if (fromCenter) {
      return { x: px - w / 2, y: py - h / 2, w, h };
    }
    // Anchor the fixed-size box at the click point.
    const dx = px - ax;
    const dy = py - ay;
    const x = dx < 0 ? px - w : ax;
    const y = dy < 0 ? py - h : ay;
    return { x, y, w, h };
  }

  let dx = px - ax;
  let dy = py - ay;

  // Fixed-ratio mode: constrain the drag to the specified ratio.
  if (style === "fixed-ratio" && fixedWidth > 0 && fixedHeight > 0) {
    const ratio = fixedWidth / fixedHeight;
    if (constrain) {
      // Square constraint overrides ratio (shift key takes priority).
      const m = Math.max(Math.abs(dx), Math.abs(dy));
      dx = dx < 0 ? -m : m;
      dy = dy < 0 ? -m : m;
    } else {
      // Constrain to the fixed ratio.
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      if (absDx / ratio > absDy) {
        // Width is the driving dimension.
        dy = dy < 0 ? -absDx / ratio : absDx / ratio;
      } else {
        // Height is the driving dimension.
        dx = dx < 0 ? -absDy * ratio : absDy * ratio;
      }
    }
  } else if (constrain) {
    const m = Math.max(Math.abs(dx), Math.abs(dy));
    dx = dx < 0 ? -m : m;
    dy = dy < 0 ? -m : m;
  }

  let x: number;
  let y: number;
  let w: number;
  let h: number;
  if (fromCenter) {
    // Anchor is the center; box extends symmetrically.
    const halfW = Math.abs(dx);
    const halfH = Math.abs(dy);
    x = ax - halfW;
    y = ay - halfH;
    w = halfW * 2;
    h = halfH * 2;
  } else {
    w = Math.abs(dx);
    h = Math.abs(dy);
    x = dx < 0 ? ax - w : ax;
    y = dy < 0 ? ay - h : ay;
  }
  return { x, y, w, h };
}
